import { Field, Label, Radio, RadioGroup } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import cancel_img from "../../../assets/Cancel Pay.svg";
import info_i from "../../../assets/i.svg";
import TicketIcon from "../../../assets/icons/TicketIcon";
import invite_img from "../../../assets/Invite Code.svg";
import ticketStarIcon from "../../../assets/ticketStarIcon.png";
import { TwButton, TwFormHelperText, TwInputGroup, TwSwitch, TwTooltip } from "../../../components/tailwind";
import { classNames, decimalDisplayValue, decimalOnChange } from "../../../helpers";
import { loadingState } from "../../../redux/reducers/loadingReducer";
import { useTranslation } from "react-i18next";

export default function PoliciesTab({
  formErrors,
  formValues,
  handleFormValueChange,
  setFormValues,
  payment,
  setTabCurrentState,
  edit = false,
  handleUpdate,
  handleCancel,
  isFromDetail = false,
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loadingReduxState = useSelector(loadingState);

  return (
    <div className={"flex flex-col gap-4"}>
      <div
        className={classNames(`flex flex-col gap-8 items-start p-6 bg-white border-2 border-bright-gray rounded-2xl`,
          isFromDetail === false ? "w-full [sm:w-3/5] " : ""
        )}
      >
        <div className={"w-full flex flex-col items-start gap-2"}>
          <TwSwitch
            title={t("myEvents.freeCancellation")}
            name={"freeCancellation"}
            checked={formValues.freeCancellation}
            onChange={() => {
              setFormValues((prevValues) => ({
                ...prevValues,
                freeCancellation: !formValues.freeCancellation,
                cancellationPeriod: (!formValues.freeCancellation) == true ? "24hrs" : "",
              }));
            }}
          />
          {formValues.freeCancellation ? (<div className={`flex flex-col gap-3`}>
            <p className={"text-sm text-gray-500"}>
              {t("myEvents.periodBeforeCancellationAllowed")}
            </p>
            <RadioGroup
              name={"cancellationPeriod"}
              value={formValues.cancellationPeriod}
              onChange={(value) => {
                setFormValues((prevValues) => ({
                  ...prevValues,
                  cancellationPeriod: value,
                }));
              }}
              className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4`}
            >
              {["1hr", "24hrs", "48hrs", "1 week"].map((plan) => (
                <Field key={plan} disabled={!formValues.freeCancellation} className="flex items-center gap-2">
                  <Radio
                    value={plan}
                    className="group flex size-5 items-center justify-center rounded-full border bg-white data-[checked]:bg-secondary data-[disabled]:bg-gray-100"
                  >
                    <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
                  </Radio>
                  <Label className="data-[disabled]:opacity-50 text-sm text-gray-500">{plan}</Label>
                </Field>
              ))}
            </RadioGroup>
            {formErrors["cancellationPeriod"] && (
              <TwFormHelperText error={true}>
                {formErrors["cancellationPeriod"]}
              </TwFormHelperText>
            )}
            <TwInputGroup
              label={t("myEvents.cancellationCharge")}
              placeholder={t("myEvents.cancellationCharge")}
              type={"text"}
              inputMode="decimal"
              name={"cancellationCharge"}
              leftIcon={"€"}
              value={decimalDisplayValue(formValues.cancellationCharge, i18n.language)}
              onChange={(event) => {
                const parsed = decimalOnChange(event.target.value, i18n.language);
                if (parsed !== null) {
                  setFormValues((prevValues) => ({
                    ...prevValues,
                    cancellationCharge: parsed,
                  }));
                }
              }}
              errors={formErrors}
              className="!py-3 md:pl-2.5"
            />
          </div>) : (
            <p className={"text-sm text-gray-500"}>
              {t("myEvents.noFreeCancellationAllowed")}
            </p>
          )}
        </div>

        <div className={"w-full flex flex-col items-start gap-2 mt-5"}>
          <TwSwitch
            title={<TwTooltip position={"right"} title={t("myEvents.privateEventTooltip")} >{t("myEvents.privateEvent")}&nbsp; <InformationCircleIcon className="size-4" /></TwTooltip>}
            name={"inviteCodeOnlyBooking"}
            checked={formValues.inviteCodeOnlyBooking}
            disabled={edit}
            onChange={() => {
              setFormValues((prevValues) => ({
                ...prevValues,
                inviteCodeOnlyBooking: !formValues.inviteCodeOnlyBooking,
              }));
            }}
          />
          {formValues.inviteCodeOnlyBooking ? (<>
            <p className={"text-sm text-gray-500 mt-1"}>
              {t("myEvents.privateEventMsg1")} <br></br>
              {t("myEvents.privateEventMsg2")}
            </p>
          </>) : (
            <p className={"text-sm text-gray-500 mt-1"}>
              {t("myEvents.eventCanBookedByAnybody2")}
            </p>
          )}

        </div>

        <div className={"w-full flex flex-col items-start gap-2 mt-5"}>
          <TwSwitch
            title={t("myEvents.orderLimit")}
            name={"orderLimit"}
            checked={formValues.orderLimit}
            onChange={(checked) => {
              setFormValues((prevValues) => ({
                ...prevValues,
                orderLimit: !formValues.orderLimit,
                ...checked == false ? { maxTicketsPerOrder: 1000 } : {},
              }));
            }}
          />
          {formValues.orderLimit ? (<TwInputGroup
            label={t("myEvents.maxTicketsAllowedPerOrder")}
            placeholder={t("myEvents.maxTicketsAllowedPerOrderPlaceholder")}
            type={"number"}
            name={"maxTicketsPerOrder"}
            leftIcon={<TicketIcon className="size-5" />}
            value={formValues.maxTicketsPerOrder}
            onChange={(event) =>
              setFormValues((prevValues) => ({
                ...prevValues,
                maxTicketsPerOrder: parseInt(event.target.value),
              }))
            }
            errors={formErrors}
            className="!py-3 md:pl-2.5"
          />) : (
            <p className={"text-sm text-gray-500 mt-1"}>
              {t("myEvents.noLimitInSingleOrder")}
            </p>
          )}
        </div>
      </div>
      {false && (<div
        className={classNames(`hidden -flex gap-2 items-start p-6 rounded-2xl  bg-white border`,
          formErrors["cancellationPeriod"] ? "border-secondary" : "border-primary",
          isFromDetail === false ? "w-full sm:w-3/5 " : ""
        )}
      >
        <img className={"w-17 mt-1.5"} src={cancel_img} alt={""} />
        <div className={"flex-1 flex flex-col gap-2 items-start"}>
          <div className={"w-full flex flex-col xl:flex-row items-start gap-3"}>
            <h4 className={"flex-1 text-nowrap text-lg font-bold"}>
              {t("myEvents.allowFreeCancellation")}
            </h4>
            <p className={"text-sm text-gray-500 mt-1"}>
              <img className={"w-4 h-4 inline mr-2 align-text-top"} src={info_i} alt={""} /> {t("myEvents.allowFreeCancellationText")}
            </p>
          </div>
          <p className={"text-sm text-gray-500"}>
            {t("myEvents.periodBeforeCancellationAllowed")}
          </p>
          <RadioGroup
            name={"cancellationPeriod"}
            value={formValues.cancellationPeriod}
            onChange={(value) => {
              setFormValues((prevValues) => ({
                ...prevValues,
                cancellationPeriod: value,
              }));
            }}
            className={"grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2"}
          >
            {["1hr", "24hrs", "48hrs", "1 week"].map((plan) => (
              <Field key={plan} disabled={!formValues.freeCancellation} className="flex items-center gap-2">
                <Radio
                  value={plan}
                  className="group flex size-5 items-center justify-center rounded-full border bg-white data-[checked]:bg-secondary data-[disabled]:bg-gray-100"
                >
                  <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
                </Radio>
                <Label className="data-[disabled]:opacity-50 text-sm text-gray-500">{plan}</Label>
              </Field>
            ))}
          </RadioGroup>
          {formErrors["cancellationPeriod"] && (
            <TwFormHelperText error={true}>
              {formErrors["cancellationPeriod"]}
            </TwFormHelperText>
          )}
        </div>
        <div className={"flex  items-start"}>
          <TwSwitch
            name={"freeCancellation"}
            checked={formValues.freeCancellation}
            onChange={() => {
              setFormValues((prevValues) => ({
                ...prevValues,
                freeCancellation: !formValues.freeCancellation,
              }));
            }}
          />
        </div>
      </div>)}
      <div
        className={classNames(`hidden -flex gap-2 items-start p-6 bg-white border border-primary rounded-2xl`,
          isFromDetail === false ? "w-full sm:w-3/5 " : ""
        )}
      >
        <img className={"w-17 mt-1.5"} src={invite_img} alt={""} />
        <div className={"flex-1 flex flex-col gap-2 items-start"}>
          <div className={"flex flex-row items-center gap-3"}>
            <h4 className={"text-lg font-bold"}>
              {t("myEvents.bookingOnlyByInviteCode")}
            </h4>
          </div>
          <p className={"text-sm text-gray-500 mt-1"}>
            {t("myEvents.privateEventMsg2")}
          </p>
          <p className={"text-xs text-gray-500 mt-1"}>
            <img className={"w-4 h-4 inline mr-2 align-text-top"} src={info_i} alt={""} /> {t("myEvents.privateEventTooltip")}
          </p>
        </div>
        <div className="flex-none w-10">
          <TwSwitch
            name={"inviteCodeOnlyBooking"}
            checked={formValues.inviteCodeOnlyBooking}
            onChange={() =>
              setFormValues((prevValues) => ({
                ...prevValues,
                inviteCodeOnlyBooking: !formValues.inviteCodeOnlyBooking,
              }))
            }
          />
        </div>
      </div>
      <div
        className={classNames(`hidden -flex gap-2 items-start p-6 bg-white border border-primary rounded-2xl`,
          isFromDetail === false ? "w-full sm:w-3/5 " : ""
        )}
      >
        <TicketIcon className={"size-6 mt-1.5"} />
        {/* <img className={"size-5 mt-1.5"} src={ticketStarIcon} alt={""} /> */}
        <div className={"flex-1 flex flex-col gap-2 items-start"}>
          <div className={"flex flex-row items-center gap-3"}>
            <h4 className={"text-lg font-bold"}>
              {t("myEvents.maxTicketsAllowedPerOrderPlaceholder")}
            </h4>
          </div>
          {/* <p className={"text-sm text-gray-500 mt-1"}>
            {t("myEvents.privateEventMsg2")}
          </p> */}
          <p className={"text-xs text-gray-500 mt-1"}>
            <TwInputGroup
              // label={t("myEvents.maxTicketsAllowedPerOrderPlaceholder")}
              placeholder={t("myEvents.maxTicketsAllowedPerOrderPlaceholder")}
              type={"number"}
              name={"maxTicketsPerOrder"}
              leftIcon={<img className="size-5" alt="" src={ticketStarIcon} />}
              value={formValues.maxTicketsPerOrder}
              onChange={(event) =>
                setFormValues((prevValues) => ({
                  ...prevValues,
                  maxTicketsPerOrder: event.target.value,
                }))
              }
              errors={formErrors}
              className="!py-3 md:pl-2.5"
            />
          </p>
        </div>
        <div className="flex-none w-10">

        </div>
      </div>
      <div className={classNames("flex gap-2 justify-center pt-5 w-full", edit ? "" : "[sm:w-3/5]")}>
        <TwButton
          variant={"secondary-outline"}
          className={"w-full sm:w-60"}
          onClick={() => {
            handleCancel()
          }}
        >
          {edit ? t("inputFieldsTitle.cancel") : t("inputFieldsTitle.back")}
        </TwButton>
        <TwButton
          variant={"secondary"}
          className={"w-full sm:w-60"}
          actionLoading={(loadingReduxState.actionLoading)}
          onClick={() => {
            if (edit) {
              handleUpdate()
            } else {
              // setTabCurrentState("5");
              setTabCurrentState("6"); //due to merge genral ans schedule tab
            }
          }}
        >
          {edit ? t("update") : t("inputFieldsTitle.continue")}
        </TwButton>
      </div>
    </div>
  );
}
